import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, adminPayments } from '../../lib/api';
import { mergeDeep, cloneDeep, formatMessage } from '../../utils/i18n';

function formatCurrency(amount, currency) {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
  });
  return formatter.format(Number(amount || 0));
}

const DEFAULT_COPY = {
  title: 'Payments & Discounts (Admin)',
  loading: 'Loading payment manager…',
  buttons: {
    close: 'Close',
    saveDefaults: 'Save Defaults',
    deleteStudent: 'Delete Payment Record',
    updateAmount: 'Update Amount',
    revokeDiscount: 'Revoke Applied Discount',
    invalidateReferral: 'Invalidate Referral Code',
    approve: 'Approve',
    reject: 'Reject',
    deletePayment: 'Delete',
    downloadReceipt: 'Download Receipt',
    previewReceipt: 'Preview',
  },
  sections: {
    defaultSettings: {
      heading: 'Default Payment Settings',
      fields: {
        defaultAmount: 'Default Tuition',
        currency: 'Currency',
        discountAmount: 'Referral Discount Amount',
        referralLevel2Amount: 'Referral #2 Amount',
        referralLevel3Amount: 'Referral #3 Amount',
      },
    },
    overview: {
      heading: 'Student Payments Overview',
      empty: 'No payment data available yet. Students will appear here after they open their Payments & Discounts tool.',
    },
  },
  summary: {
    outstanding: 'Outstanding Balance',
    approved: 'Approved Payments',
    totalTuition: 'Total Tuition',
    outstandingPill: 'Outstanding',
  },
  studentCard: {
    defaultName: 'Student',
    accountCreated: 'Account created: {date}',
    setTotalTuition: 'Set Total Tuition',
    referralHeading: 'Referral Discounts',
    appliedHeading: 'Applied to this student',
    appliedEmpty: 'No referral discount applied.',
    appliedCode: 'Code:',
    appliedValue: 'Value:',
    appliedDate: 'Applied:',
    appliedProvider: 'Provided by:',
    referralGeneratedHeading: 'Referral code generated',
    referralGeneratedEmpty: 'No referral code generated.',
    referralCode: 'Code:',
    referralValue: 'Value:',
    referralGenerated: 'Generated:',
    referralStatus: 'Status:',
    referralStatusAssigned: 'Assigned to {name}',
    referralStatusAssignedFallback: 'another student',
    referralStatusInvalidated: 'Invalidated',
    referralStatusAvailable: 'Available to share',
    referralStatusApproved: 'Approved',
    paymentsHeading: 'Payments',
    paymentsEmpty: 'No payments submitted.',
    columns: {
      date: 'Date',
      amount: 'Amount',
      method: 'Method',
      status: 'Status',
      notes: 'Notes',
      receipt: 'Receipt',
      actions: 'Actions',
    },
  },
  preview: {
    title: 'Receipt Preview',
    close: 'Close Preview',
    unsupported: 'Preview unavailable. Download the file to view it.',
    loading: 'Loading receipt preview…',
    error: 'Failed to load receipt preview.',
  },
  messages: {
    settingsUpdated: 'Default payment settings updated. New students will use these values.',
    studentAmountUpdated: 'Student payment amount updated.',
    paymentApproved: 'Payment approved.',
    paymentRejected: 'Payment rejected.',
    paymentUpdated: 'Payment updated.',
    paymentRemoved: 'Payment removed.',
    discountRevoked: 'Applied discount revoked.',
    referralInvalidated: 'Referral code invalidated.',
    referralApproved: 'Referral discount approved.',
    studentRecordDeleted: 'Student payment record deleted.',
    referralApplied: 'Referral code {code} applied successfully.',
    referralGenerated: 'Referral code {code} generated! Share it with a new student so they can receive {amount} off their tuition.',
    referralGeneratedNoAmount: 'Referral code {code} generated! Share it with a new student.',
    paymentSubmitted: 'Payment submitted for approval.',
  },
  errors: {
    loadOverview: 'Failed to load payments overview.',
    defaultAmountInvalid: 'Enter a valid default amount.',
    discountAmountInvalid: 'Enter a valid referral discount amount.',
    referralLevel2AmountInvalid: 'Enter a valid Referral #2 amount.',
    referralLevel3AmountInvalid: 'Enter a valid Referral #3 amount.',
    studentAmountInvalid: 'Enter a valid amount for the student.',
    enterReferralCode: 'Enter a referral code to apply.',
    paymentAmountInvalid: 'Enter a valid payment amount.',
    amountExceeds: 'Amount exceeds remaining balance.',
    requestFailed: 'Request failed.',
    referralGenerateFailed: 'Failed to generate referral code.',
    referralApplyFailed: 'Failed to apply referral code.',
    paymentSubmitFailed: 'Failed to submit payment.',
  },
  confirm: {
    deleteStudent: 'Delete this student payment record? This removes payments and resets discounts.',
  },
  statuses: {
    APPROVED: 'Approved',
    PENDING: 'Pending',
    REJECTED: 'Rejected',
  },
};

export default function AdminPaymentsModal({ onClose, copy: copyOverrides }) {
  const copy = useMemo(() => mergeDeep(cloneDeep(DEFAULT_COPY), copyOverrides || {}), [copyOverrides]);
  const [snapshot, setSnapshot] = useState(null);
  const [defaultAmount, setDefaultAmount] = useState(2500);
  const [discountAmount, setDiscountAmount] = useState(250);
  const [referralLevel2Amount, setReferralLevel2Amount] = useState(0);
  const [referralLevel3Amount, setReferralLevel3Amount] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [amountDrafts, setAmountDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [previewData, setPreviewData] = useState({
    open: false,
    url: '',
    mimeType: '',
    fileName: '',
    downloadUrl: '',
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewUrlRef = useRef(null);

  const getStoredToken = useCallback(() => (
    localStorage.getItem('accessToken') || localStorage.getItem('go2cdl_access_token') || ''
  ), []);

  const buildReceiptUrl = useCallback((downloadPath) => {
    if (!downloadPath) return null;
    const token = getStoredToken();
    const separator = downloadPath.includes('?') ? '&' : '?';
    const tokenQuery = token ? `${separator}token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}${downloadPath}${tokenQuery}`;
  }, [getStoredToken]);

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const syncSettings = useCallback((settings) => {
    if (!settings) return;
    setDefaultAmount(Number(settings.defaultAmount ?? 0));
    setDiscountAmount(Number(settings.discountAmount ?? 0));
    setReferralLevel2Amount(Number(settings.referralLevel2Amount ?? 0));
    setReferralLevel3Amount(Number(settings.referralLevel3Amount ?? 0));
    setCurrency(settings.currency || 'USD');
  }, []);

  const applySnapshot = useCallback((response, { sync = false, successMessage } = {}) => {
    if (sync && response?.settings) {
      syncSettings(response.settings);
    }
    setSnapshot(response);
    if (successMessage) {
      setMessage(successMessage);
    }
    setError('');
  }, [syncSettings]);

  const runAction = useCallback(async (exec, { successMessage, syncSettings: sync = false, errorMessage } = {}) => {
    setWorking(true);
    setMessage('');
    setError('');
    try {
      const response = await exec();
      applySnapshot(response, { sync, successMessage });
      return response;
    } catch (err) {
      console.error('Payments admin action failed:', err);
      setError(err?.message || errorMessage || copy.errors.requestFailed);
      return null;
    } finally {
      setWorking(false);
    }
  }, [applySnapshot, copy.errors.requestFailed]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminPayments.getOverview();
      applySnapshot(response, { sync: true });
    } catch (err) {
      console.error('Failed to load payments overview:', err);
      setError(err?.message || copy.errors.loadOverview);
    } finally {
      setLoading(false);
    }
  }, [applySnapshot, copy.errors.loadOverview]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => () => {
    revokePreviewUrl();
  }, [revokePreviewUrl]);

  const students = useMemo(() => snapshot?.students || [], [snapshot]);
  const settingsCurrency = snapshot?.settings?.currency || currency;

  const handleSaveSettings = useCallback((e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    const amount = Number(defaultAmount);
    if (Number.isNaN(amount) || amount < 0) {
      setError(copy.errors.defaultAmountInvalid);
      return;
    }

    const discountValue = Number(discountAmount);
    if (Number.isNaN(discountValue) || discountValue < 0) {
      setError(copy.errors.discountAmountInvalid);
      return;
    }

    const level2Value = Number(referralLevel2Amount);
    if (Number.isNaN(level2Value) || level2Value < 0) {
      setError(copy.errors.referralLevel2AmountInvalid);
      return;
    }

    const level3Value = Number(referralLevel3Amount);
    if (Number.isNaN(level3Value) || level3Value < 0) {
      setError(copy.errors.referralLevel3AmountInvalid);
      return;
    }

    runAction(
      () => adminPayments.updateSettings({
        defaultAmount: amount,
        discountAmount: discountValue,
        referralLevel2Amount: level2Value,
        referralLevel3Amount: level3Value,
        currency: currency || 'USD',
      }),
      { successMessage: copy.messages.settingsUpdated, syncSettings: true }
    );
  }, [
    copy.errors.defaultAmountInvalid,
    copy.errors.discountAmountInvalid,
    copy.errors.referralLevel2AmountInvalid,
    copy.errors.referralLevel3AmountInvalid,
    copy.messages.settingsUpdated,
    defaultAmount,
    discountAmount,
    referralLevel2Amount,
    referralLevel3Amount,
    currency,
    runAction,
  ]);

  const handleUpdateStudentAmount = useCallback((studentId) => {
    setMessage('');
    setError('');
    const draftValue = Number(amountDrafts[studentId]);
    if (Number.isNaN(draftValue) || draftValue < 0) {
      setError(copy.errors.studentAmountInvalid);
      return;
    }

    runAction(
      () => adminPayments.updateStudentTuition(studentId, draftValue),
      { successMessage: copy.messages.studentAmountUpdated }
    ).then((response) => {
      if (response) {
        setAmountDrafts(prev => ({ ...prev, [studentId]: '' }));
      }
    });
  }, [amountDrafts, copy.errors.studentAmountInvalid, copy.messages.studentAmountUpdated, runAction]);

  const handleApprovePayment = useCallback((paymentId, status) => {
    const successMessage = status === 'APPROVED'
      ? copy.messages.paymentApproved
      : status === 'REJECTED'
        ? copy.messages.paymentRejected
        : copy.messages.paymentUpdated;

    runAction(
      () => adminPayments.updatePaymentStatus(paymentId, status),
      { successMessage }
    );
  }, [copy.messages.paymentApproved, copy.messages.paymentRejected, copy.messages.paymentUpdated, runAction]);

  const handleDeletePayment = useCallback((paymentId) => {
    runAction(
      () => adminPayments.deletePayment(paymentId),
      { successMessage: copy.messages.paymentRemoved }
    );
  }, [copy.messages.paymentRemoved, runAction]);

  const handleRevokeAppliedDiscount = useCallback((studentId) => {
    runAction(
      () => adminPayments.revokeAppliedDiscount(studentId),
      { successMessage: copy.messages.discountRevoked }
    );
  }, [copy.messages.discountRevoked, runAction]);

  const handleInvalidateReferralCode = useCallback((studentId) => {
    runAction(
      () => adminPayments.invalidateReferralCode(studentId),
      { successMessage: copy.messages.referralInvalidated }
    );
  }, [copy.messages.referralInvalidated, runAction]);

  const handleApproveReferralDiscount = useCallback((studentId) => {
    runAction(
      () => adminPayments.approveReferralDiscount(studentId),
      { successMessage: copy.messages.referralApproved || 'Referral discount approved.' }
    );
  }, [copy.messages.referralApproved, runAction]);

  const handleDeleteStudentRecord = useCallback((studentId) => {
    setMessage('');
    setError('');

    const confirmed = typeof window === 'undefined' ? true : window.confirm(copy.confirm.deleteStudent);
    if (!confirmed) return;

    runAction(
      () => adminPayments.deleteStudentRecord(studentId),
      { successMessage: copy.messages.studentRecordDeleted }
    ).then((response) => {
      if (response) {
        setAmountDrafts(prev => {
          if (!prev[studentId]) return prev;
          const nextDrafts = { ...prev };
          delete nextDrafts[studentId];
          return nextDrafts;
        });
      }
    });
  }, [copy.confirm.deleteStudent, copy.messages.studentRecordDeleted, runAction]);

  const handleClosePreview = useCallback(() => {
    revokePreviewUrl();
    setPreviewData({ open: false, url: '', mimeType: '', fileName: '', downloadUrl: '' });
    setPreviewError('');
    setPreviewLoading(false);
  }, [revokePreviewUrl]);

  const handlePreviewReceipt = useCallback(async (payment) => {
    if (!payment?.receipt?.downloadPath) return;
    revokePreviewUrl();
    setPreviewError('');
    setPreviewLoading(true);
    setPreviewData({
      open: true,
      url: '',
      mimeType: payment.receipt.mimeType || '',
      fileName: payment.receipt.fileName || 'payment-receipt',
      downloadUrl: buildReceiptUrl(payment.receipt.downloadPath),
    });

    try {
      const token = getStoredToken();
      const response = await fetch(`${API_BASE}${payment.receipt.downloadPath}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const text = await response.text().catch(() => null);
        throw new Error(text || copy.preview.error);
      }

      const blob = await response.blob();
      const mimeType = response.headers.get('content-type') || payment.receipt.mimeType || 'application/octet-stream';
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      setPreviewData({
        open: true,
        url: objectUrl,
        mimeType,
        fileName: payment.receipt.fileName || 'payment-receipt',
        downloadUrl: buildReceiptUrl(payment.receipt.downloadPath),
      });
    } catch (err) {
      console.error('Failed to load receipt preview:', err);
      setPreviewError(err?.message || copy.preview.error);
    } finally {
      setPreviewLoading(false);
    }
  }, [buildReceiptUrl, copy.preview.error, getStoredToken, revokePreviewUrl]);

  if (loading && !snapshot) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center">
          <div className="mb-3">{copy.loading}</div>
          <button type="button" onClick={onClose} className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{copy.title}</h2>
          </div>
          <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
        </div>

        <div className="p-6 space-y-8">
          <section className="border border-slate-200 rounded-xl p-4 bg-slate-50">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">{copy.sections.defaultSettings.heading}</h3>
            <form onSubmit={handleSaveSettings} className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">{copy.sections.defaultSettings.fields.defaultAmount}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={defaultAmount}
                  onChange={(e) => setDefaultAmount(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">{copy.sections.defaultSettings.fields.currency}</label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  className="w-full rounded-lg border px-3 py-2"
                  maxLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">{copy.sections.defaultSettings.fields.discountAmount}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">{copy.sections.defaultSettings.fields.referralLevel2Amount}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={referralLevel2Amount}
                  onChange={(e) => setReferralLevel2Amount(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">{copy.sections.defaultSettings.fields.referralLevel3Amount}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={referralLevel3Amount}
                  onChange={(e) => setReferralLevel3Amount(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2"
                />
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <button
                  type="submit"
                  disabled={working}
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {copy.buttons.saveDefaults}
                </button>
              </div>
            </form>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{copy.sections.overview.heading}</h3>
            {students.length === 0 ? (
              <div className="border border-slate-200 rounded-xl p-4 text-sm text-slate-500 bg-slate-50">
                {copy.sections.overview.empty}
              </div>
            ) : (
              <div className="space-y-4">
                {students.map(record => {
                  const summary = record.summary || {
                    approvedTotal: 0,
                    pendingTotal: 0,
                    discountValue: 0,
                    outstanding: 0,
                    effectiveTotal: 0,
                  };
                  const studentId = record.studentId || record.id;
                  const generatedReferral = record.generatedReferral || null;
                  const studentName = record.student?.name || copy.studentCard.defaultName;
                  const accountCreated = record.student?.createdAt
                    ? new Date(record.student.createdAt).toLocaleDateString()
                    : '—';
                  return (
                    <div key={studentId} className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900">{studentName}</h4>
                          <div className="text-sm text-slate-500">{record.student?.email}</div>
                          <div className="text-xs text-slate-400">{formatMessage(copy.studentCard.accountCreated, { date: accountCreated })}</div>
                        </div>
                        <div className="flex flex-col gap-2 lg:items-end">
                          <div className="flex gap-3">
                            <SummaryPill label={copy.summary.totalTuition} value={formatCurrency(record.totalDue, settingsCurrency)} color="blue" />
                            <SummaryPill label={copy.summary.outstandingPill} value={formatCurrency(summary.outstanding, settingsCurrency)} color={summary.outstanding > 0 ? 'red' : 'green'} />
                          </div>
                          <button
                            onClick={() => handleDeleteStudentRecord(studentId)}
                            disabled={working}
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {copy.buttons.deleteStudent}
                          </button>
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleUpdateStudentAmount(studentId);
                            }}
                            className="space-y-2"
                          >
                            <label className="block text-sm font-medium text-slate-600">{copy.studentCard.setTotalTuition}</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={amountDrafts[studentId] ?? record.totalDue}
                              onChange={(e) => setAmountDrafts(prev => ({ ...prev, [studentId]: e.target.value }))}
                              className="w-full rounded-lg border px-3 py-2"
                            />
                            <button
                              type="submit"
                              disabled={working}
                              className="w-full px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {copy.buttons.updateAmount}
                            </button>
                          </form>
                        </div>

                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">{copy.studentCard.referralHeading}</h5>
                          <div className="space-y-3 text-sm text-slate-600">
                            <div className="rounded-lg bg-white border border-slate-200 p-3">
                              <div className="font-semibold text-slate-700 mb-1">{copy.studentCard.appliedHeading}</div>
                              {record.appliedDiscount ? (
                                <div className="space-y-1">
                                  <div>{copy.studentCard.appliedCode} <span className="inline-flex items-center px-2 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-semibold tracking-wide">{record.appliedDiscount.code}</span></div>
                                  <div>{copy.studentCard.appliedValue} <span className="font-semibold text-slate-900">{formatCurrency(record.appliedDiscount.amount, settingsCurrency)}</span></div>
                                  <div>{copy.studentCard.appliedDate} {record.appliedDiscount.assignedAt ? new Date(record.appliedDiscount.assignedAt).toLocaleString() : '—'}</div>
                                  <div>{copy.studentCard.appliedProvider} {record.appliedDiscount.generatedBy?.name || record.appliedDiscount.generatedBy?.email || '—'}</div>
                                  {record.appliedDiscount.status !== 'APPROVED' && (
                                    <button
                                      onClick={() => handleApproveReferralDiscount(studentId)}
                                      disabled={working}
                                      className="mt-2 text-xs text-green-600 hover:text-green-700 disabled:opacity-60 disabled:cursor-not-allowed block"
                                    >
                                      Approve Referral Discount
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRevokeAppliedDiscount(studentId)}
                                    disabled={working}
                                    className="mt-2 text-xs text-red-600 hover:text-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {copy.buttons.revokeDiscount}
                                  </button>
                                </div>
                              ) : (
                                <div>{copy.studentCard.appliedEmpty}</div>
                              )}
                            </div>

                            <div className="rounded-lg bg-white border border-slate-200 p-3">
                              <div className="font-semibold text-slate-700 mb-1">{copy.studentCard.referralGeneratedHeading}</div>
                              {generatedReferral ? (
                                <div className="space-y-1">
                                  <div>{copy.studentCard.referralCode} <span className="inline-flex items-center px-2 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-semibold tracking-wide">{generatedReferral.code}</span></div>
                                  <div>{copy.studentCard.referralValue} <span className="font-semibold text-slate-900">{formatCurrency(generatedReferral.amount, settingsCurrency)}</span></div>
                                  <div>{copy.studentCard.referralGenerated} {generatedReferral.generatedAt ? new Date(generatedReferral.generatedAt).toLocaleString() : '—'}</div>
                                  <div>{copy.studentCard.referralStatus} <span className={`font-semibold ${generatedReferral.assignedTo ? 'text-green-600' : generatedReferral.status === 'INVALIDATED' ? 'text-red-600' : 'text-amber-600'}`}>
                                    {generatedReferral.status === 'APPROVED'
                                      ? copy.studentCard.referralStatusApproved || 'Approved'
                                      : generatedReferral.assignedTo
                                        ? formatMessage(copy.studentCard.referralStatusAssigned, {
                                            name: generatedReferral.assignedTo.name || generatedReferral.assignedTo.email || copy.studentCard.referralStatusAssignedFallback,
                                          })
                                        : generatedReferral.status === 'INVALIDATED'
                                          ? copy.studentCard.referralStatusInvalidated
                                          : copy.studentCard.referralStatusAvailable}
                                  </span></div>
                                  {generatedReferral.assignedTo && generatedReferral.status !== 'APPROVED' && (
                                    <button
                                      onClick={() => handleApproveReferralDiscount(generatedReferral.assignedTo.id)}
                                      disabled={working}
                                      className="mt-2 text-xs text-green-600 hover:text-green-700 disabled:opacity-60 disabled:cursor-not-allowed block"
                                    >
                                      Approve Referral Discount
                                    </button>
                                  )}
                                  {!generatedReferral.assignedTo && generatedReferral.status === 'AVAILABLE' && (
                                    <button
                                      onClick={() => handleInvalidateReferralCode(studentId)}
                                      disabled={working}
                                      className="mt-2 text-xs text-red-600 hover:text-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      {copy.buttons.invalidateReferral}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div>{copy.studentCard.referralGeneratedEmpty}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">{copy.studentCard.paymentsHeading}</h5>
                        {(record.payments || []).length === 0 ? (
                          <div className="text-sm text-slate-500">{copy.studentCard.paymentsEmpty}</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500">
                                  <th className="py-2 pr-3">{copy.studentCard.columns.date}</th>
                                  <th className="py-2 pr-3">{copy.studentCard.columns.amount}</th>
                                  <th className="py-2 pr-3">{copy.studentCard.columns.method}</th>
                                  <th className="py-2 pr-3">{copy.studentCard.columns.status}</th>
                                  <th className="py-2 pr-3">{copy.studentCard.columns.notes}</th>
                                  <th className="py-2 pr-3">{copy.studentCard.columns.receipt || copy.buttons.downloadReceipt}</th>
                                  <th className="py-2">{copy.studentCard.columns.actions}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {[...record.payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(payment => (
                                  <tr key={payment.id}>
                                    <td className="py-2 pr-3 text-slate-600">{new Date(payment.createdAt).toLocaleString()}</td>
                                    <td className="py-2 pr-3 text-slate-900 font-medium">{formatCurrency(payment.amount, settingsCurrency)}</td>
                                    <td className="py-2 pr-3 text-slate-600">{payment.method}</td>
                                    <td className="py-2 pr-3">
                                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${payment.status === 'APPROVED' ? 'bg-green-100 text-green-700' : payment.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {payment.status}
                                      </span>
                                    </td>
                                    <td className="py-2 pr-3 text-slate-500">{payment.note || '—'}</td>
                                    <td className="py-2 pr-3 text-slate-500">
                                      {payment.receipt?.downloadPath ? (
                                        <div className="flex items-center gap-3">
                                          <button
                                            type="button"
                                            onClick={() => handlePreviewReceipt(payment)}
                                            className="text-indigo-600 hover:text-indigo-700 font-medium"
                                          >
                                            {copy.buttons.previewReceipt}
                                          </button>
                                          <span className="text-slate-300">|</span>
                                          <a
                                            href={buildReceiptUrl(payment.receipt.downloadPath)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-700 font-medium"
                                          >
                                            {copy.buttons.downloadReceipt}
                                          </a>
                                        </div>
                                      ) : '—'}
                                    </td>
                                    <td className="py-2 flex flex-wrap gap-2">
                                      <button
                                        onClick={() => handleApprovePayment(payment.id, 'APPROVED')}
                                        disabled={working}
                                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => handleApprovePayment(payment.id, 'REJECTED')}
                                        disabled={working}
                                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                      >
                                        Reject
                                      </button>
                                      <button
                                        onClick={() => handleDeletePayment(payment.id)}
                                        disabled={working}
                                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {(message || error) && (
            <div className={`${error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'} rounded-lg px-4 py-3 text-sm`}>
              {error || message}
            </div>
          )}
        </div>
      </div>

      {previewData.open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 py-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">{copy.preview.title}</h3>
                {previewData.fileName && (
                  <p className="text-sm text-slate-500 break-all mt-1">{previewData.fileName}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClosePreview}
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                aria-label={copy.preview.close}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-slate-100 p-4 flex items-center justify-center">
              {previewLoading ? (
                <div className="text-sm text-slate-600">{copy.preview.loading}</div>
              ) : previewError ? (
                <div className="text-center space-y-3">
                  <p className="text-sm text-red-600">{previewError}</p>
                  {previewData.downloadUrl && (
                    <a
                      href={previewData.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                    >
                      {copy.buttons.downloadReceipt}
                    </a>
                  )}
                </div>
              ) : previewData.url ? (
                previewData.mimeType?.startsWith('image/') ? (
                  <img
                    src={previewData.url}
                    alt={previewData.fileName || 'Receipt preview'}
                    className="max-h-[70vh] max-w-full rounded-lg shadow"
                  />
                ) : previewData.mimeType?.includes('pdf') ? (
                  <iframe
                    title="Receipt preview"
                    src={previewData.url}
                    className="w-full h-[70vh] bg-white rounded-lg"
                  />
                ) : (
                  <div className="text-center space-y-3 text-sm text-slate-600">
                    <p>{copy.preview.unsupported}</p>
                    {previewData.downloadUrl && (
                      <a
                        href={previewData.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                      >
                        {copy.buttons.downloadReceipt}
                      </a>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center space-y-3 text-sm text-slate-600">
                  <p>{copy.preview.unsupported}</p>
                  {previewData.downloadUrl && (
                    <a
                      href={previewData.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                    >
                      {copy.buttons.downloadReceipt}
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-white flex items-center justify-between">
              {previewData.downloadUrl ? (
                <a
                  href={previewData.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {copy.buttons.downloadReceipt}
                </a>
              ) : <span />}
              <button
                type="button"
                onClick={handleClosePreview}
                className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-300"
              >
                {copy.preview.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, value, color = 'blue' }) {
  const palette = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
  };
  return (
    <div className={`rounded-xl border px-4 py-2 text-sm font-semibold ${palette[color] || palette.blue}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div>{value}</div>
    </div>
  );
}
